/**
 * User Profile Dropdown Component
 * คอมโพเนนต์ Dropdown สำหรับโปรไฟล์ผู้ใช้
 * รวมการตั้งค่าภาษาและธีม
 */

import { useTranslation } from 'react-i18next'
import { LogOut, Sun, Moon, Monitor, Globe, Settings } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/features/auth'
import { useThemeStore } from '@/features/theme'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

export function UserProfileDropdown() {
  const { t, i18n } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const { theme, setTheme } = useThemeStore()

  const displayName = user?.fullName ?? t('auth.user')
  const displayDomain = user?.domainId ?? t('auth.accountingOffice')
  const initials = displayName[0]?.toUpperCase() ?? 'U'

  const handleLanguageChange = (lang: string) => {
    void i18n.changeLanguage(lang)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-2 rounded-full p-1 pr-2 transition-colors sm:gap-2.5',
            'hover:bg-white/10 focus:ring-2 focus:ring-white/30 focus:outline-none'
          )}
          aria-label={t('auth.userMenu')}
        >
          {/* User Info - Hidden on mobile */}
          <div className="hidden text-right sm:block">
            <div className="text-xs font-semibold text-white">{displayName}</div>
            <div className="text-[10px] text-white/60">{displayDomain}</div>
          </div>

          {/* Avatar */}
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-sm font-bold text-white"
            aria-hidden="true"
          >
            {initials}
          </div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-64" align="end" sideOffset={8}>
        {/* User Info Section */}
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm leading-none font-medium">{displayName}</p>
            <p className="text-muted-foreground text-xs leading-none">{displayDomain}</p>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Language Selection */}
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-2 text-xs">
            <Globe className="h-3.5 w-3.5" />
            {t('settings.language')}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup value={i18n.language} onValueChange={handleLanguageChange}>
            <DropdownMenuRadioItem value="th" className="text-sm">
              <span className="mr-2">🇹🇭</span>
              {t('languages.th')}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="en" className="text-sm">
              <span className="mr-2">🇬🇧</span>
              {t('languages.en')}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        {/* Theme Selection */}
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-2 text-xs">
            <Sun className="h-3.5 w-3.5" />
            {t('settings.theme')}
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={theme}
            onValueChange={(value) => {
              setTheme(value as 'light' | 'dark' | 'system')
            }}
          >
            <DropdownMenuRadioItem value="light" className="text-sm">
              <Sun className="mr-2 h-4 w-4" />
              {t('themes.light')}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark" className="text-sm">
              <Moon className="mr-2 h-4 w-4" />
              {t('themes.dark')}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system" className="text-sm">
              <Monitor className="mr-2 h-4 w-4" />
              {t('themes.system')}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        {/* Settings */}
        <DropdownMenuItem asChild>
          <Link to="/settings/account/profile" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4" />
            {t('nav.settings')}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Logout */}
        <DropdownMenuItem
          onClick={logout}
          className="text-destructive focus:text-destructive cursor-pointer"
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t('auth.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
